import {Component, computed, inject, input, ChangeDetectionStrategy} from "@angular/core"
import {SceneNodes} from "@cm/template-nodes"
import {SceneAggregationMode} from "@app/template-editor/components/template-tree-view-type-selector/template-tree-view-type-selector.component"
import {bytesToSize} from "@cm/utils"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"

const colorSlots = ["color", "normal", "baseColor", "clearcoatNormal", "emission", "subsurfaceColor", "subsurfaceRadius", "tangent"]

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-tree-scene-node-aggregation",
    imports: [],
    templateUrl: "./template-tree-scene-node-aggregation.component.html",
    styleUrl: "./template-tree-scene-node-aggregation.component.scss",
})
export class TemplateTreeSceneNodeAggregationComponent {
    private readonly sceneManagerService = inject(SceneManagerService)
    readonly $input = input.required<SceneNodes.SceneNode[] | SceneNodes.SceneNode>({alias: "input"})
    readonly $sceneAggregationMode = input.required<SceneAggregationMode>({alias: "sceneAggregationMode"})
    readonly $sceneNodes = computed(() => {
        const input = this.$input()
        return Array.isArray(input) ? input : [input]
    })
    readonly $value = computed(() => {
        const sceneNodes = this.$sceneNodes()
        const aggregationMode = this.$sceneAggregationMode()
        const lodType = this.sceneManagerService.$lodType()
        const sceneOptions = this.sceneManagerService.$sceneOptions()

        let result = 0 as number | null
        if (aggregationMode === "triangles") {
            const getMeshFaces = (node: SceneNodes.Mesh): number => node.completeMeshData.reified.indices.length / 3

            sceneNodes.forEach((node) => {
                if (result === null) return
                if (SceneNodes.Seam.is(node)) {
                    const {reified} = node.completeCurveData
                    if (!reified) {
                        result = null
                        return
                    }

                    const meshesAggregates = node.item.reduce((acc, mesh) => acc + getMeshFaces(mesh), 0)
                    result += reified.scales.length * meshesAggregates
                }

                if (aggregationMode === "triangles") {
                    if (SceneNodes.Mesh.is(node)) result += getMeshFaces(node)
                }
            })
        } else if (aggregationMode === "downloadSize") {
            const datasetIds = new Set<number>()

            sceneNodes.forEach((node) => {
                if (result === null) return

                if (SceneNodes.DataObjectReference.is(node)) {
                    if (node.origin.type === "materialAssignment" && lodType !== "pathTraced" && sceneOptions?.enableRealtimeMaterials === false) return

                    const {dataObject} = node
                    const {legacyId, size} = dataObject
                    if (size == null) {
                        result = null
                        return
                    }

                    if (datasetIds.has(legacyId)) return
                    datasetIds.add(legacyId)

                    result += size
                }
            })
            return result
        } else if (aggregationMode === "memoryUsage") {
            const materialAssignmentNodeIds = new Set<string>()
            const meshIds = new Set<string>()

            const getMeshSize = (node: SceneNodes.Mesh): number => {
                if (meshIds.has(node.id)) return 0
                meshIds.add(node.id)

                const {vertices, uvs, indices} = node.completeMeshData.reified
                const numVertices = vertices.length / 3
                const indexBufferSize = numVertices > 65535 ? 4 : 2
                return numVertices * (3 + 3 + uvs.length * 2) * 4 + indices.length * indexBufferSize
            }

            sceneNodes.forEach((node) => {
                if (result === null) return

                if (SceneNodes.DataObjectReference.is(node)) {
                    const {dataObject} = node
                    const {width, height, mediaType} = dataObject
                    if (node.origin.type !== "materialAssignment" && node.origin.type !== "hdriLight") return
                    if (width == null || height == null) {
                        result = null
                        return
                    }

                    if (node.origin.type === "materialAssignment") {
                        if (lodType !== "pathTraced" && sceneOptions?.enableRealtimeMaterials === false) return
                        const {nodeId, slots, final} = node.origin

                        if (materialAssignmentNodeIds.has(nodeId) || !final) return
                        materialAssignmentNodeIds.add(nodeId)

                        if (mediaType === "image/x-exr" || mediaType === "image/exr" || mediaType === "image/vnd.radiance") {
                            const numChannels = mediaType === "image/vnd.radiance" || colorSlots.some((slot) => slots.has(slot)) ? 4 : 1
                            const channelBytes = mediaType === "image/vnd.radiance" ? 4 : 2
                            result += numChannels * channelBytes * width * height
                        } else result += 4 * 1 * width * height
                    } else if (node.origin.type === "hdriLight") {
                        const channelBytes = lodType === "pathTraced" ? 4 : 2
                        result += 4 * channelBytes * width * height
                    }
                } else if (SceneNodes.Mesh.is(node)) {
                    result += getMeshSize(node)
                } else if (SceneNodes.Seam.is(node)) {
                    const {reified} = node.completeCurveData
                    if (!reified) {
                        result = null
                        return
                    }
                    result += node.item.reduce((acc, mesh) => acc + getMeshSize(mesh), 0) + 32 * reified.scales.length * 16 * 4
                }
            })
        }

        return result
    })
    readonly $displayValue = computed(() => {
        const value = this.$value()

        if (value === null) return "?"
        else if (value === 0) return undefined

        const aggregationMode = this.$sceneAggregationMode()

        if (aggregationMode === "triangles") {
            if (value > 1000) {
                return (value / 1000).toFixed(0) + "k"
            } else if (value > 0) {
                return value.toString()
            }
        } else if (aggregationMode === "downloadSize" || aggregationMode === "memoryUsage") {
            return bytesToSize(value)
        }

        return undefined
    })
}
